import { useState, useEffect } from 'react'
import { useNavigate, useOutletContext, useParams } from 'react-router-dom'
import { HiOutlineArrowLeft } from 'react-icons/hi'
import { MdShare } from 'react-icons/md'
import { MdMoreVert } from 'react-icons/md'
import { baseurl } from '../Url'
import styles from './JobDetailsView.module.css'
import ApplyButton from '../Opportunities/ApplyButtton'
import Loader from '../Loder/Loader'

const normalizeMediaToUrls = (value) => {
  if (!value) return []
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed ? [trimmed] : []
  }
  if (Array.isArray(value)) {
    return value
      .flatMap((item) => normalizeMediaToUrls(item))
      .filter((url, index, arr) => arr.indexOf(url) === index)
  }
  if (typeof value === 'object') {
    const possibleKeys = ['url', 'src', 'image', 'thumbnail', 'path']
    for (const key of possibleKeys) {
      const nested = normalizeMediaToUrls(value[key])
      if (nested.length > 0) return nested
    }
  }
  return []
}

const getMediaType = (url = '') => {
  const cleanUrl = url.split('?')[0].toLowerCase()
  if (cleanUrl.endsWith('.mp4') || cleanUrl.endsWith('.webm') || cleanUrl.endsWith('.ogg')) return 'video'
  if (cleanUrl.endsWith('.pdf')) return 'pdf'
  return 'image'
}

const isStudyOpportunity = (data) =>
  String(data?.type ?? data?.opportunity_type ?? '')
    .toLowerCase()
    .trim() === 'study'

const firstNonEmpty = (data, keys) => {
  if (!data) return ''
  for (const key of keys) {
    const v = data[key]
    if (v != null && String(v).trim() !== '') return String(v).trim()
  }
  return ''
}

const formatStudyFeeRange = (data) => {
  const min = data?.fees_min ?? data?.feesMin
  const max = data?.fees_max ?? data?.feesMax
  const nMin = Number(min)
  const nMax = Number(max)
  const hasMin = min != null && min !== '' && Number.isFinite(nMin)
  const hasMax = max != null && max !== '' && Number.isFinite(nMax)
  if (hasMin && hasMax) return `₹${nMin.toLocaleString('en-IN')} – ₹${nMax.toLocaleString('en-IN')}`
  if (hasMin) return `From ₹${nMin.toLocaleString('en-IN')}`
  if (hasMax) return `Up to ₹${nMax.toLocaleString('en-IN')}`
  return '—'
}

const normalizeAdmissionSteps = (raw) => {
  if (!raw) return []
  if (!Array.isArray(raw)) return []
  return raw
    .map((item) => {
      if (typeof item === 'string') return item.trim()
      if (item && typeof item === 'object') {
        return String(item.text ?? item.title ?? item.step ?? item.description ?? '').trim()
      }
      return String(item ?? '').trim()
    })
    .filter(Boolean)
}


function JobDetailsView() {
  const navigate = useNavigate()
  const { jobId } = useParams()
  const { onMenuToggle, sidebarOpen } = useOutletContext() || {}

  const [jobData, setJobData] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState('')
  const [shareCopied, setShareCopied] = useState(false)
  const [currentThumbnailIndex, setCurrentThumbnailIndex] = useState(0)

  const thumbnailList = normalizeMediaToUrls(jobData?.thumbnail)

  useEffect(() => {
    if (jobId) {
      fetchAllData()
    } else {
      setErrorMessage('No vacancy ID provided')
      setIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId])

  useEffect(() => {
    setCurrentThumbnailIndex(0)
  }, [thumbnailList.length])

  const fetchAllData = async () => {
    setIsLoading(true)
    setErrorMessage('')

    const employerId = localStorage.getItem('USERID')

    if (!employerId) {
      setErrorMessage('Please login to view vacancy details')
      setIsLoading(false)
      return
    }

    if (!jobId) {
      setErrorMessage('Invalid vacancy ID')
      setIsLoading(false)
      return
    }

    try {
      // Fetch vacancy details
      const vacancyResponse = await fetch(`${baseurl}/vacancies/${jobId}?member_id=${employerId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (!vacancyResponse.ok) {
        const errorData = await vacancyResponse.json().catch(() => ({}))
        setErrorMessage(errorData.message || `Server error: ${vacancyResponse.status}`)
        setIsLoading(false)
        return
      }

      const vacancyData = await vacancyResponse.json()
      if (vacancyData.status === 'success') {
        setJobData(vacancyData.data)
      } else {
        setErrorMessage(vacancyData.message || 'Failed to load vacancy details')
        setIsLoading(false)
        return
      }

    } catch (error) {
      console.error('Error fetching data:', error)
      setErrorMessage('An error occurred while loading data: ' + (error.message || 'Unknown error'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleBack = () => {
    navigate('/opportunities')
  }

  const checkUser = async () => {
    const memberId = localStorage.getItem('USERID')
    if (!memberId) {
      navigate('/login', { state: { from: `/job-view/${jobId}` } })
      throw new Error('Not logged in')
    }
    const res = await fetch(`${baseurl}/check/user`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: memberId }),
    })
    const data = await res.json()
    if (!data.status) {
      navigate('/login', { state: { from: `/job-view/${jobId}` } })
      throw new Error('User not valid')
    }
  }

  const handleShare = async () => {
    const userId = jobData?.employer_id || localStorage.getItem('USERID') || jobId
    const currentUserId = localStorage.getItem('USERID')
    let shareUrl = `${baseurl}/opportunities/share/${userId}/${jobId}`
    try {
      if (currentUserId) {
        try {
          const token = localStorage.getItem('access-token')
          let bearer = token
          if (token && typeof token === 'string' && token.startsWith('{')) {
            try {
              const parsed = JSON.parse(token)
              bearer = parsed?.token ?? parsed ?? token
            } catch {
              bearer = token
            }
          }

          const res = await fetch(`${baseurl}/referral/link`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
            },
            body: JSON.stringify({
              user_id: currentUserId,
              asset_category: 'JOB',
              asset_id: jobId,
            }),
          })

          const data = await res.json()
          if (data.status && data.data?.referral_code) {
            shareUrl = `${shareUrl}?ref=${data.data.referral_code}`
            localStorage.setItem('duno_referral_code', data.data.referral_code)
            if (data.data.referrer_user_id) {
              localStorage.setItem('duno_referral_user_id', data.data.referrer_user_id)
            } else {
              localStorage.setItem('duno_referral_user_id', currentUserId)
            }
            localStorage.setItem(
              'duno_referral_asset_category',
              data.data.referral_asset_category || 'JOB'
            )
            if (data.data.referral_asset_id || jobId) {
              localStorage.setItem('duno_referral_asset_id', data.data.referral_asset_id || jobId)
            }
          }
        } catch (err) {
          console.warn('Referral link fetch failed for vacancy share:', err)
        }
      }

      await navigator.clipboard.writeText(shareUrl)
      setShareCopied(true)
      setTimeout(() => setShareCopied(false), 2000)
    } catch (error) {
      console.error('Share failed:', error)
    }
  }

  // Format evaluation steps for display
  const getEvaluationSteps = (evaluationSteps) => {
    if (!evaluationSteps) return []
    const evaluations = []
    if (evaluationSteps.theoreticalAssessments) evaluations.push('Theoretical Assessments')
    if (evaluationSteps.practicalAssessments) evaluations.push('Practical Assessments')
    if (evaluationSteps.behaviouralAssessments) evaluations.push('Behavioural Assessments')
    if (evaluationSteps.technicalInterview) evaluations.push('Technical Interview')
    if (evaluationSteps.hrInterview) evaluations.push('HR Interview')
    return evaluations
  }

  const renderBulletSection = (title, items) => (
    <section className={styles.contentSection}>
      <h2 className={styles.sectionTitle}>{title}</h2>
      {items?.length > 0 ? (
        <div className={styles.contentCard}>
          <div className={styles.bulletListWrap}>
            <ul className={styles.bulletList}>
              {items.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </div>
        </div>
      ) : (
        <p className={styles.emptyInlineText}>None specified</p>
      )}
    </section>
  )

  const renderStudyTextSection = (title, text) => {
    const t = String(text ?? '').trim()
    if (!t) return null
    return (
      <section className={styles.contentSection}>
        <h2 className={styles.sectionTitle}>{title}</h2>
        <div className={styles.contentCard}>
          <p className={styles.detailText}>{t}</p>
        </div>
      </section>
    )
  }

  if (isLoading) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.container}>
          <div className={styles.loadingState}>
            <Loader variant="dashboard" />
          </div>
        </div>
      </div>
    )
  }

  if (errorMessage) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.container}>
          <div className={styles.errorState}>
            <p>{errorMessage}</p>
            <button className={styles.backButton} onClick={handleBack}>
              <HiOutlineArrowLeft /> Back to Opportunities
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (!jobData) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.container}>
          <div className={styles.loadingState}>
            <p>Vacancy not found</p>
            <button className={styles.backButton} onClick={handleBack}>
              <HiOutlineArrowLeft /> Back to Opportunities
            </button>
          </div>
        </div>
      </div>
    )
  }

  const primaryThumbnail = thumbnailList[currentThumbnailIndex]
  const evaluationItems = getEvaluationSteps(jobData.evaluation_steps)
  const primaryMediaType = getMediaType(primaryThumbnail)

  const isStudy = isStudyOpportunity(jobData)
  const displayHeading = isStudy
    ? firstNonEmpty(jobData, ['course_title', 'courseTitle', 'job_title', 'name']) || 'Program'
    : jobData.job_title || 'Opportunity'
  const studySubtitle = firstNonEmpty(jobData, ['program_type', 'programType'])
  const studyCollegeName = firstNonEmpty(jobData, ['college_name', 'collegeName'])
  const studyDepartment = firstNonEmpty(jobData, ['department'])
  const studyDuration = firstNonEmpty(jobData, ['duration'])
  const courseDescription = firstNonEmpty(jobData, [
    'course_description',
    'courseDescription',
    'job_description',
    'description',
  ])
  const admissionSteps = normalizeAdmissionSteps(jobData.admission_process ?? jobData.admissionProcess)
  const overviewCards = isStudy ? (
    <>
      {/* <div className={styles.overviewCard}>
        <p className={styles.overviewCardLabel}>Program type</p>
        <p className={styles.overviewCardValue}>
          {firstNonEmpty(jobData, ['program_type', 'programType']) || '—'}
        </p>
      </div> */}
      {/* <div className={styles.overviewCard}>
        <p className={styles.overviewCardLabel}>Department</p>
        <p className={styles.overviewCardValue}>{firstNonEmpty(jobData, ['department']) || '—'}</p>
      </div> */}
      {/* <div className={styles.overviewCard}>
        <p className={styles.overviewCardLabel}>College</p>
        <p className={styles.overviewCardValue}>
          {firstNonEmpty(jobData, ['college_name', 'collegeName']) || '—'}
        </p>
      </div> */}
      <div className={`${styles.overviewCard} ${styles.universityOverviewCard}`}>
        <p className={styles.overviewCardLabel}>University</p>
        <p className={styles.overviewCardValue}>{firstNonEmpty(jobData, ['university']) || '—'}</p>
      </div>
      <div className={`${styles.overviewCard} ${styles.studyModeOverviewCard}`}>
        <p className={styles.overviewCardLabel}>Study mode</p>
        <p className={styles.overviewCardValue}>
          {firstNonEmpty(jobData, ['study_mode', 'studyMode']) || '—'}
        </p>
      </div>
      {/* <div className={styles.overviewCard}>
        <p className={styles.overviewCardLabel}>Duration</p>
        <p className={styles.overviewCardValue}>{firstNonEmpty(jobData, ['duration']) || '—'}</p>
      </div> */}
      <div className={`${styles.overviewCard} ${styles.studyFeesOverviewCard}`}>
        <p className={styles.overviewCardLabel}>Fee range (annual)</p>
        <p className={styles.overviewCardValue}>{formatStudyFeeRange(jobData)}</p>
      </div>
      <div className={`${styles.overviewCard} ${styles.studySeatsOverviewCard}`}>
        <p className={styles.overviewCardLabel}>No of Seats</p>
        <p className={styles.overviewCardValue}>
          {jobData.number_of_positions ?? jobData.numberOfPositions ?? 0}
        </p>
      </div>
    </>
  ) : (
    <>
      {/* <div className={styles.overviewCard}>
        <p className={styles.overviewCardLabel}>Designation</p>
        <p className={styles.overviewCardValue}>{jobData.designation || '—'}</p>
      </div> */}
      <div className={styles.overviewCard}>
        <p className={styles.overviewCardLabel}>Positions</p>
        <p className={styles.overviewCardValue}>{jobData.number_of_positions || 0}</p>
      </div>
      <div className={styles.overviewCard}>
        <p className={styles.overviewCardLabel}>Employment Category</p>
        <p className={styles.overviewCardValue}>{jobData.category_name || jobData.opportunity_type || '—'}</p>
      </div>
      <div className={styles.overviewCard}>
        <p className={styles.overviewCardLabel}>Work Mode</p>
        <p className={styles.overviewCardValue}>{jobData.work_mode || '—'}</p>
      </div>
      <div className={styles.overviewCard}>
        <p className={styles.overviewCardLabel}>Location</p>
        <p className={styles.overviewCardValue}>{jobData.work_location || '—'}</p>
      </div>
      <div className={styles.overviewCard}>
        <p className={styles.overviewCardLabel}>Salary Range</p>
        <p className={styles.overviewCardValue}>
          {jobData.payment_min || jobData.payment_max
            ? `₹${(jobData.payment_min || 0).toLocaleString('en-IN')} – ₹${(jobData.payment_max || 0).toLocaleString('en-IN')}`
            : '—'}
        </p>
      </div>
    </>
  )

  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        <div className={styles.mainCard}>
          {/* Header */}
          <header className={styles.header}>
            <div className={styles.headerTop}>
              <div className={styles.menuBackGroup}>
                {typeof onMenuToggle === 'function' && !sidebarOpen && (
                  <button
                    type="button"
                    className={styles.mobileHeaderMenuBtn}
                    onClick={onMenuToggle}
                    aria-label="Open menu"
                  >
                    <MdMoreVert size={20} />
                  </button>
                )}
                <button className={styles.backButton} onClick={handleBack}>
                  <HiOutlineArrowLeft /> Back to Opportunities
                </button>
              </div>
              <div className={styles.headerActions}>
                <button
                  className={`${styles.shareBtn} ${shareCopied ? styles.shareCopied : ''}`}
                  onClick={handleShare}
                  title={shareCopied ? 'Copied!' : 'Share'}
                >
                  <MdShare size={18} />
                  <span>{shareCopied ? 'Copied!' : 'Share'}</span>
                </button>
                <div className={styles.applyBtnWrap}>
                  <ApplyButton
                    vacancyId={jobData.id}
                    initiallyApplied={jobData.has_applied}
                    onBeforeApply={checkUser}
                  />
                </div>
              </div>
            </div>
            <div
              className={`${styles.headerContent} ${primaryThumbnail ? '' : styles.headerContentNoMedia}`}
            >
              {primaryThumbnail && (
                <div className={styles.thumbnailWrap}>
                  <div className={styles.galleryLayout}>
                    <div className={styles.thumbnailRail} aria-label="Image preview list">
                      {thumbnailList.map((thumb, index) => (
                        (() => {
                          const mediaType = getMediaType(thumb)
                          return (
                        <button
                          key={`${thumb}-${index}`}
                          type="button"
                          className={`${styles.thumbnailPreviewBtn} ${index === currentThumbnailIndex ? styles.thumbnailPreviewBtnActive : ''}`}
                          onClick={() => setCurrentThumbnailIndex(index)}
                          aria-label={`Show ${mediaType} ${index + 1}`}
                        >
                          {mediaType === 'video' ? (
                            <>
                              <video
                                src={thumb}
                                className={styles.thumbnailPreviewImage}
                                muted
                                playsInline
                                preload="metadata"
                              />
                              <span className={styles.videoThumbLabel}>VIDEO</span>
                            </>
                          ) : mediaType === 'pdf' ? (
                            <div className={styles.pdfThumbPreview}>
                              <span>PDF</span>
                            </div>
                          ) : (
                            <img
                              src={thumb}
                              alt={`Thumbnail ${index + 1}`}
                              className={styles.thumbnailPreviewImage}
                            />
                          )}
                        </button>
                          )
                        })()
                      ))}
                    </div>
                    <div className={styles.mainImageFrame}>
                      {primaryMediaType === 'video' ? (
                        <video
                          src={primaryThumbnail}
                          controls
                          className={styles.thumbnailImage}
                          preload="metadata"
                        />
                      ) : primaryMediaType === 'pdf' ? (
                        <div className={styles.pdfPreviewCard}>
                          <div className={styles.pdfPreviewBadge}>PDF</div>
                          <p className={styles.pdfPreviewText}>
                            {displayHeading ? `${displayHeading} attachment` : 'Opportunity attachment'}
                          </p>
                          <button
                            type="button"
                            className={styles.pdfPreviewButton}
                            onClick={() => window.open(primaryThumbnail, '_blank', 'noopener,noreferrer')}
                          >
                            Open PDF
                          </button>
                        </div>
                      ) : (
                        <img
                          src={primaryThumbnail}
                          alt={displayHeading ? `${displayHeading} thumbnail` : 'Opportunity thumbnail'}
                          className={styles.thumbnailImage}
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}
              <div className={styles.headerMeta}>
                <h1 className={styles.mainTitle}>{displayHeading}</h1>
                {isStudy ? (
                  studySubtitle ? <p className={styles.jobSubtitle}>{studySubtitle}</p> : null
                ) : (
                  jobData.designation ? <p className={styles.jobSubtitle}>{jobData.designation}</p> : null
                )}
                {isStudy && (
                  <div className={styles.studyMetaGroup}>
                    {studyCollegeName ? (
                      <p className={styles.studyMetaLine}>
                        <span className={styles.studyMetaLabel}>College:</span>
                        <span className={styles.studyMetaValue}>{studyCollegeName}</span>
                      </p>
                    ) : null}
                    {studyDepartment ? (
                      <p className={styles.studyMetaLine}>
                        <span className={styles.studyMetaLabel}>Department:</span>
                        <span className={styles.studyMetaValue}>{studyDepartment}</span>
                      </p>
                    ) : null}
                    {studyDuration ? (
                      <p className={styles.studyMetaLine}>
                        <span className={styles.studyMetaLabel}>Duration:</span>
                        <span className={styles.studyMetaValue}>{studyDuration}</span>
                      </p>
                    ) : null}
                  </div>
                )}
            <div className={styles.overviewCardsColumn}>{overviewCards}</div>

                {/* <p className={styles.jobShortDescription}>
                  {isStudy
                    ? courseDescription || 'No description provided.'
                    : jobData.job_description || 'No description provided.'}
                </p> */}
              </div>
            </div>
            {/* <div className={styles.overviewCardsColumn}>{overviewCards}</div> */}
          </header>

          {isStudy ? (
            <>
              <section className={styles.contentSection}>
                <h2 className={styles.sectionTitle}>About the program</h2>
                <div className={styles.contentCard}>
                  <p className={styles.detailText}>
                    {courseDescription || 'No description provided.'}
                  </p>
                </div>
              </section>
              {renderBulletSection('Admission process', admissionSteps)}
              {renderStudyTextSection('Scholarship', jobData.scholarship)}
              {renderStudyTextSection(
                'Financial aid',
                jobData.financial_aid ?? jobData.financialAid
              )}
            </>
          ) : (
            <>
              <section className={styles.contentSection}>
                <h2 className={styles.sectionTitle}>About the Role</h2>
                <div className={styles.contentCard}>
                  <p className={styles.detailText}>{jobData.job_description || 'No description provided.'}</p>
                </div>
              </section>

              <div className={styles.detailColumns}>
                <div className={styles.detailColumn}>
                  {renderBulletSection('Pre-requisites', jobData.prerequisites)}
                  {renderBulletSection('Mandatory', jobData.mandatory)}
                  {renderBulletSection('Good to Have', jobData.good_to_have)}
                </div>

                <div className={styles.detailColumn}>
                  {renderBulletSection('Benefits', jobData.other_benefits)}
                  <section className={styles.contentSection}>
                    <h2 className={styles.sectionTitle}>Evaluation Process</h2>
                    {evaluationItems.length > 0 ? (
                      <div className={styles.contentCard}>
                        <div className={styles.evaluationsList}>
                          {evaluationItems.map((item, i) => (
                            <div key={i} className={styles.evaluationItem}>
                              <span className={styles.checkboxChecked}>✓</span>
                              <span>{item}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p className={styles.emptyInlineText}>None specified</p>
                    )}
                  </section>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default JobDetailsView

